import { Component, ElementRef, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { form, maxLength } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { firstValueFrom, Observable, of, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoImageHandlerService, TositeJaNaytettavaSivut } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { AreYouSureDialog, AreYouSureDialogData } from '../../../_jaettu-angular/_components/are-you-sure.dialog'
import { asetaArvo, nollaa } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { KirjattavaKuitti } from '../../../_jaettu-lemonator/model/kirjanpito'
import {
  LLM_TOSITE_LUKU_LADATTAVAT_MEDIATYYPIT,
  LLM_TOSITE_LUKU_TIEDOSTON_MAX_BASE64_TAVUT,
  LlmTositeLukuTestisetti,
  LlmTositeLukuTestitapaus,
} from '../../../_jaettu-lemonator/model/llm-tosite-luku-testaus'
import { KirjanpitoUriService } from '../../../_jaettu-lemonator/service/kirjanpito-uri.service'
import { LlmTositeLukuMetatiedotService } from '../../../_jaettu-lemonator/service/llm-tosite-luku-metatiedot.service'
import { LisaaTositteetTestisettiinDialog, LisaaTositteetTestisettiinDialogData, LisaaTositteetTestisettiinDialogResult, LisattavaTositeKohde } from './lisaa-tositteet-testisettiin.dialog'
import { callLlmTositeLukuTestaus } from './llm-tosite-luku-testaus.client'
import { asetaViitetiedotLomakkeelle, lueMetatiedotLomakkeelta, luoMetatiedotFormGroup, PAIVAMAARA_REGEX } from './metatiedot-form.component'
import { laskeOdotettuToimittajaTeksti, metatiedotToimittajaValinnaksi } from './toimittaja-valinta'

interface LadatunTiedostonEsikatselu {
  nimi: string
  onPdf: boolean
  url: string
}

const _MEDIATYYPPI_PAATTEELLE: { [paate: string]: string } = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  pdf: 'application/pdf',
}

@Component({
  selector: 'app-tosite-luku-setit',
  templateUrl: './tosite-luku-setit.component.html',
  styleUrls: ['./tosite-luku-setit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeLukuSetitComponent implements OnInit, OnDestroy {
  @ViewChild('tositeContainer', { static: false }) tositeContainerRef: ElementRef<HTMLDivElement>
  @ViewChild('tiedostoInput', { static: false }) tiedostoInputRef: ElementRef<HTMLInputElement>

  readonly tyhjaKirjausObservable: Observable<null> = of(null)
  readonly hyvaksytytTiedostotyypit = `.jpg,.jpeg,.png,.webp,.pdf,${LLM_TOSITE_LUKU_LADATTAVAT_MEDIATYYPIT.join(',')}`

  readonly setit = signal<LlmTositeLukuTestisetti[]>([])
  readonly setitLataa = signal(false)
  uusiSettiNimiKentta = form(signal(''), polku => {
    maxLength(polku, 120)
  })
  readonly luoSettiKaynnissa = signal(false)

  readonly valittuSetti = signal<LlmTositeLukuTestisetti | null>(null)
  readonly testitapaukset = signal<LlmTositeLukuTestitapaus[]>([])
  readonly testitapauksetLataa = signal(false)
  readonly poistetaan = signal<ReadonlySet<string>>(new Set())
  readonly paivitetaanRekisterit = signal(false)

  readonly valittuTestitapaus = signal<LlmTositeLukuTestitapaus | null>(null)
  readonly naytettavatTositteetJaSivut = signal<TositeJaNaytettavaSivut[] | null>([])
  readonly ladatutEsikatselut = signal<LadatunTiedostonEsikatselu[] | null>([])
  readonly tallennetaan = signal(false)

  metatiedotForm = luoMetatiedotFormGroup()
  viitepaivaKentta = form(signal(''))
  readonly odotettuToimittajaTeksti = signal('')

  private _ngUnsubscribe = new Subject<void>()

  constructor(
    private _firebase: FirebaseLemonator,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjanpitoImageHandlerService: KirjanpitoImageHandlerService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _metatiedotService: LlmTositeLukuMetatiedotService,
  ) {}

  async ngOnInit() {
    this.metatiedotForm.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => this._paivitaOdotettuToimittajaTeksti())
    await this._lataaSetit()
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async luoSetti() {
    const nimi = this.uusiSettiNimiKentta().value().trim()
    if (!nimi || this.luoSettiKaynnissa()) {
      return
    }
    this.luoSettiKaynnissa.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'luoSetti', { nimi })
      if (vastaus.e === 'nimi-varattu') {
        this._snackbar.open('Samanniminen setti on jo olemassa', 'OK', { duration: 5000 })
        return
      }
      if (vastaus.e || !vastaus.avain) {
        this._snackbar.open('Setin luonti epäonnistui', 'OK', { duration: 5000 })
        return
      }
      asetaArvo(this.uusiSettiNimiKentta, '')
      await this._lataaSetit()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Setin luonti epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this.luoSettiKaynnissa.set(false)
    }
  }

  async poistaSetti(setti: LlmTositeLukuTestisetti) {
    const data: AreYouSureDialogData = {
      header: 'Poista setti',
      text: `Poista setti "${setti.nimi}" ja sen ${setti.testitapausLkm} testitapausta? Ladatut tiedostot poistetaan. Tätä ei voi peruuttaa.`,
      rightAction: 'Poista',
    }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure) {
      return
    }
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'poistaSetti', { avain: setti.avain })
      if (vastaus.e) {
        this._snackbar.open('Setin poisto epäonnistui', 'OK', { duration: 5000 })
        return
      }
      await this._lataaSetit()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Setin poisto epäonnistui', 'OK', { duration: 5000 })
    }
  }

  async avaaSetti(setti: LlmTositeLukuTestisetti) {
    this.valittuSetti.set(setti)
    this.valittuTestitapaus.set(null)
    this.naytettavatTositteetJaSivut.set([])
    await this._lataaTestitapaukset(setti.avain)
  }

  suljeSetti() {
    this.valittuSetti.set(null)
    this.valittuTestitapaus.set(null)
    this.testitapaukset.set([])
    this.naytettavatTositteetJaSivut.set([])
    this._lataaSetit()
  }

  avaaTiedostovalitsin() {
    this.tiedostoInputRef?.nativeElement.click()
  }

  async tiedostotValittu(event: Event) {
    const input = event.target as HTMLInputElement
    const tiedostot = Array.from(input.files ?? [])
    input.value = ''
    if (!tiedostot.length) {
      return
    }
    const kohteet: LisattavaTositeKohde[] = []
    const hylatyt: string[] = []
    for (const tiedosto of tiedostot) {
      const paate = tiedosto.name.split('.').pop()?.toLowerCase() ?? ''
      const mediaTyyppi = (LLM_TOSITE_LUKU_LADATTAVAT_MEDIATYYPIT as readonly string[]).includes(tiedosto.type) ? tiedosto.type : _MEDIATYYPPI_PAATTEELLE[paate]
      const base64Koko = Math.ceil(tiedosto.size / 3) * 4
      if (!mediaTyyppi) {
        hylatyt.push(`${tiedosto.name} (tuntematon tyyppi)`)
      } else if (base64Koko > LLM_TOSITE_LUKU_TIEDOSTON_MAX_BASE64_TAVUT) {
        hylatyt.push(`${tiedosto.name} (liian suuri)`)
      } else {
        kohteet.push({ tyyppi: 'ladattu', tiedosto, mediaTyyppi })
      }
    }
    if (hylatyt.length) {
      this._snackbar.open(`Hylättiin: ${hylatyt.join(', ')}`, 'OK', { duration: 8000 })
    }
    if (!kohteet.length) {
      return
    }
    const data: LisaaTositteetTestisettiinDialogData = { kohteet }
    const tulos = await firstValueFrom(
      this._dialog
        .open<LisaaTositteetTestisettiinDialog, LisaaTositteetTestisettiinDialogData, LisaaTositteetTestisettiinDialogResult>(LisaaTositteetTestisettiinDialog, {
          data,
          maxWidth: '95vw',
          disableClose: true,
        })
        .afterClosed(),
    )
    if (tulos?.lisatty) {
      await this._lataaSetit()
      if (this.valittuSetti()) {
        this.valittuSetti.set(this.setit().find(s => s.avain === this.valittuSetti().avain) ?? this.valittuSetti())
        await this._lataaTestitapaukset(this.valittuSetti().avain)
      }
    }
  }

  get onMuutoksia(): boolean {
    return this.metatiedotForm.dirty || this.viitepaivaKentta().dirty()
  }

  async valitseTestitapaus(testitapaus: LlmTositeLukuTestitapaus) {
    if (this.valittuTestitapaus()?.avain === testitapaus.avain) {
      return
    }
    if (this.valittuTestitapaus() && this.onMuutoksia) {
      const data: AreYouSureDialogData = { header: 'Tallentamattomia muutoksia', text: 'Metatietoihin on tallentamattomia muutoksia. Hylätäänkö ne?', rightAction: 'Hylkää' }
      const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
      if (!sure) {
        return
      }
    }
    this.valittuTestitapaus.set(testitapaus)
    this._asetaLomake(testitapaus)
    await this._lataaTosite(testitapaus)
  }

  async tallennaMetatiedot() {
    const testitapaus = this.valittuTestitapaus()
    if (!testitapaus || this.tallennetaan() || !this.onMuutoksia) {
      return
    }
    const metatiedot = lueMetatiedotLomakkeelta(this.metatiedotForm)
    if (metatiedot.paivamaara && !PAIVAMAARA_REGEX.test(metatiedot.paivamaara)) {
      this._snackbar.open('Päivämäärän muoto on YYYY-MM-DD', 'OK', { duration: 5000 })
      return
    }
    const viitepaiva = this.viitepaivaKentta().value().trim()
    if (!PAIVAMAARA_REGEX.test(viitepaiva)) {
      this._snackbar.open('Viitepäivän muoto on YYYY-MM-DD', 'OK', { duration: 5000 })
      return
    }
    this.tallennetaan.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'paivitaTestitapausMetatiedot', { avain: testitapaus.avain, metatiedot, viitepaiva })
      if (vastaus.e === 'missing-data') {
        this._snackbar.open('Tallennus epäonnistui: tarkista, että päivämäärä ja viitepäivä ovat todellisia päiviä', 'OK', { duration: 8000 })
        return
      }
      if (vastaus.e) {
        this._snackbar.open(`Tallennus epäonnistui: ${vastaus.e}`, 'OK', { duration: 5000 })
        return
      }
      const paivitetty = { ...testitapaus, metatiedot, viitepaiva }
      this.testitapaukset.update(rivit => rivit.map(rivi => (rivi === testitapaus ? paivitetty : rivi)))
      if (this.valittuTestitapaus() === testitapaus) {
        this.valittuTestitapaus.set(paivitetty)
      }
      this.metatiedotForm.markAsPristine()
      this.viitepaivaKentta().reset()
      this._snackbar.open('Metatiedot tallennettu', 'OK', { duration: 3000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tallennus epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  peruMuutokset() {
    if (this.valittuTestitapaus()) {
      this._asetaLomake(this.valittuTestitapaus())
    }
  }

  kopioiViitetiedosta(kentta: 'toimittaja' | 'businessId' | 'paivamaara' | 'summa') {
    const v = this.valittuTestitapaus()?.tositteenTiedot
    if (!v) {
      return
    }
    asetaViitetiedotLomakkeelle(this.metatiedotForm, kentta, v)
  }

  async poistaTestitapaus(testitapaus: LlmTositeLukuTestitapaus) {
    const data: AreYouSureDialogData = { header: 'Poista setistä', text: `Poista "${testitapaus.nimi}" setistä? Syötetyt metatiedot menetetään.`, rightAction: 'Poista' }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure || !this.valittuSetti()) {
      return
    }
    this.poistetaan.update(avaimet => new Set(avaimet).add(testitapaus.avain))
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'poistaTestitapaus', { avain: testitapaus.avain })
      if (vastaus.e) {
        this._snackbar.open('Poisto epäonnistui', 'OK', { duration: 5000 })
        return
      }
      if (this.valittuTestitapaus()?.avain === testitapaus.avain) {
        this.valittuTestitapaus.set(null)
        this.naytettavatTositteetJaSivut.set([])
      }
      await this._lataaTestitapaukset(this.valittuSetti().avain)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Poisto epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this.poistetaan.update(avaimet => new Set([...avaimet].filter(avain => avain !== testitapaus.avain)))
    }
  }

  async paivitaToimittajaRekisterit(testitapaus?: LlmTositeLukuTestitapaus) {
    if (!this.valittuSetti() || this.paivitetaanRekisterit()) {
      return
    }
    const teksti = testitapaus
      ? `Lataa tositteen "${testitapaus.nimi}" toimittajarekisteri uudelleen lemonaidista?`
      : `Lataa setin "${this.valittuSetti().nimi}" kaikkien lemonaid-testitapausten toimittajarekisterit uudelleen lemonaidista?`
    const data: AreYouSureDialogData = {
      header: 'Päivitä toimittajarekisterit',
      text: `${teksti} Metatietoihin ei kosketa; automaattisesti johdettu odotettu toimittaja päätellään uudesta rekisteristä ajon aikana, käsin valittu säilyy.`,
      rightAction: 'Päivitä',
    }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure) {
      return
    }
    this.paivitetaanRekisterit.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'paivitaToimittajaRekisterit', { settiAvain: this.valittuSetti().avain, testitapausAvain: testitapaus?.avain })
      if (vastaus.e) {
        this._snackbar.open(`Päivitys epäonnistui: ${vastaus.e}`, 'OK', { duration: 5000 })
        return
      }
      if (vastaus.valittuToimittajaPuuttuu.length) {
        this._snackbar.open(`Rekisteri päivitetty ${vastaus.paivitetty} testitapaukselle. Käsin valittu toimittaja ei enää löydy rekisteristä: ${vastaus.valittuToimittajaPuuttuu.join(', ')}`, 'OK', {
          duration: 10000,
        })
      } else {
        this._snackbar.open(`Toimittajarekisteri päivitetty ${vastaus.paivitetty} testitapaukselle`, 'OK', { duration: 4000 })
      }
      const valittuAvain = this.valittuTestitapaus()?.avain ?? null
      await this._lataaTestitapaukset(this.valittuSetti().avain)
      if (valittuAvain) {
        this.valittuTestitapaus.set(this.testitapaukset().find(t => t.avain === valittuAvain) ?? null)
        this._paivitaOdotettuToimittajaTeksti()
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Päivitys epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this.paivitetaanRekisterit.set(false)
    }
  }

  /** Ennen y-tunnusten lisäämistä tallennetuissa snapshoteissa kenttää ei ole lainkaan. */
  rekisteristaPuuttuuYtunnukset(rivi: LlmTositeLukuTestitapaus): boolean {
    return rivi.toimittajaRekisteri.length > 0 && rivi.toimittajaRekisteri.every(toimittaja => toimittaja.ytunnus === undefined)
  }

  metatiedotTiivistelma(rivi: LlmTositeLukuTestitapaus): string {
    const m = rivi.metatiedot
    const osat = [m.toimittaja ?? 'toimittaja: null', m.businessId ?? 'tunnus: null']
    if (m.paivamaara) {
      osat.push(m.paivamaara)
    }
    if (typeof m.summa === 'number') {
      osat.push(`${m.summa.toFixed(2)} €`)
    }
    if (m.tyyppi) {
      osat.push(m.tyyppi)
    }
    if (m.toimittajaAvain !== undefined) {
      const valittu = m.toimittajaAvain === null ? null : rivi.toimittajaRekisteri.find(t => t.avain === m.toimittajaAvain)
      osat.push(`rekisterissä: ${m.toimittajaAvain === null ? 'ei (valittu)' : valittu ? `${valittu.nimi} (valittu)` : `${m.toimittajaAvain} (valittu, puuttuu rekisteristä)`}`)
    }
    return osat.join(' | ')
  }

  private _asetaLomake(testitapaus: LlmTositeLukuTestitapaus) {
    const metatiedot = testitapaus.metatiedot
    nollaa(this.viitepaivaKentta, testitapaus.viitepaiva ?? '')
    this.metatiedotForm.reset({
      toimittaja: metatiedot.toimittaja ?? '',
      businessId: metatiedot.businessId ?? '',
      paivamaara: metatiedot.paivamaara ?? '',
      summa: typeof metatiedot.summa === 'number' ? metatiedot.summa : null,
      toimittajaValinta: metatiedotToimittajaValinnaksi(metatiedot),
      tyyppi: metatiedot.tyyppi ?? '',
    })
  }

  private _paivitaOdotettuToimittajaTeksti() {
    const testitapaus = this.valittuTestitapaus()
    this.odotettuToimittajaTeksti.set(
      laskeOdotettuToimittajaTeksti(
        this._metatiedotService,
        !!testitapaus && testitapaus.lahde === 'lemonaid',
        false,
        testitapaus?.toimittajaRekisteri ?? [],
        lueMetatiedotLomakkeelta(this.metatiedotForm),
      ),
    )
  }

  private async _lataaTosite(testitapaus: LlmTositeLukuTestitapaus) {
    this.ladatutEsikatselut.set([])
    if (testitapaus.lahde === 'ladattu') {
      this.naytettavatTositteetJaSivut.set([])
      await this._lataaLadatutTiedostot(testitapaus)
      return
    }
    if (!testitapaus.asiakasAvain || !testitapaus.tositeAvain) {
      this.naytettavatTositteetJaSivut.set([])
      return
    }
    this.naytettavatTositteetJaSivut.set(null)
    try {
      const uri = this._kirjanpitoUriService.annaKirjattavanFirestoreTositteenUriAvaimella(testitapaus.asiakasAvain, testitapaus.tositeAvain)
      const kirjattavaKuitti = await this._firebase.firestoreDoc<KirjattavaKuitti>(uri).get()
      const tosite = kirjattavaKuitti
        ? await this._kirjanpitoImageHandlerService.muutaKuittiTositteeksi({ avain: testitapaus.asiakasAvain, asiakasId: Number(testitapaus.asiakasId) }, kirjattavaKuitti)
        : null
      if (this.valittuTestitapaus()?.avain !== testitapaus.avain) {
        return
      }
      this.naytettavatTositteetJaSivut.set(tosite ? [tosite] : [])
    } catch (err) {
      this._errorHandler.handleError(err)
      if (this.valittuTestitapaus()?.avain === testitapaus.avain) {
        this.naytettavatTositteetJaSivut.set([])
      }
    }
  }

  private async _lataaLadatutTiedostot(testitapaus: LlmTositeLukuTestitapaus) {
    this.ladatutEsikatselut.set(null)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeLadatutTiedostot', { testitapausAvain: testitapaus.avain })
      if (this.valittuTestitapaus()?.avain !== testitapaus.avain) {
        return
      }
      this.ladatutEsikatselut.set(
        (vastaus.tiedostot ?? []).map(t => ({
          nimi: t.nimi,
          onPdf: t.mediaTyyppi === 'application/pdf',
          url: `data:${t.mediaTyyppi};base64,${t.base64Sisalto}`,
        })),
      )
    } catch (err) {
      this._errorHandler.handleError(err)
      if (this.valittuTestitapaus()?.avain === testitapaus.avain) {
        this.ladatutEsikatselut.set([])
      }
    }
  }

  private async _lataaSetit() {
    this.setitLataa.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeSetit', {})
      if (vastaus.e) {
        this._snackbar.open('Settien haku epäonnistui', 'OK', { duration: 5000 })
        return
      }
      this.setit.set(vastaus.setit)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.setitLataa.set(false)
    }
  }

  private async _lataaTestitapaukset(settiAvain: string) {
    this.testitapauksetLataa.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeSettiTestitapaukset', { settiAvain })
      if (vastaus.e) {
        this._snackbar.open('Testitapausten haku epäonnistui', 'OK', { duration: 5000 })
        return
      }
      this.testitapaukset.set(vastaus.rivit)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.testitapauksetLataa.set(false)
    }
  }
}
